/**
 * VenueFlow MediaInfo Service
 *
 * Full spec validation for ALL file sizes without freezing the browser.
 *
 * Strategy:
 * - Small files (<100MB): MediaInfo runs on the main thread (fast, no overhead)
 * - Large files (≥100MB): MediaInfo runs in a Web Worker (off main thread)
 * - Both paths produce the same track data and feed it into the same
 *   validateVideoTracks() function, so every spec is checked regardless of size.
 * - If both MediaInfo and the worker fail (CDN down, blocked), falls back to
 *   HTMLVideoElement browser probe (dimensions + duration only).
 *
 * Supports both spec formats:
 * - IPTV style: spec.dimensions = [{ width, height }]
 * - BigScreen style: spec.videoSpec.width, spec.videoSpec.height
 */

// ============================================================
// MEDIAINFO READY PROMISE (main thread, with timeout)
// ============================================================
const MEDIAINFO_TIMEOUT_MS = 5000;

export const MediaInfoReady = new Promise((resolve) => {
  const timeout = setTimeout(() => {
    console.warn(
      `[MediaInfo] Timed out after ${MEDIAINFO_TIMEOUT_MS / 1000}s waiting for mediainfo.js — will use worker or browser fallback`
    );
    resolve(null);
  }, MEDIAINFO_TIMEOUT_MS);

  const wait = () => {
    if (window.MediaInfo) {
      clearTimeout(timeout);
      resolve(window.MediaInfo);
    } else {
      setTimeout(wait, 50);
    }
  };
  wait();
});

// ============================================================
// WEB WORKER MANAGER (disposable per failure)
// Sends File objects to mediainfo-worker.js and resolves with
// the parsed tracks array. Worker is spawned lazily on first use.
// If ANY analysis fails (timeout, error, corrupt file), the worker
// is terminated and a fresh one spawned for the next file.
// This is necessary because WASM internal state can get corrupted
// by a failed analysis, causing subsequent files to hang.
// ============================================================
let workerInstance = null;
let workerIdCounter = 0;
const workerCallbacks = new Map();

function killWorker() {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
  }
  // Reject any pending callbacks
  for (const [id, cb] of workerCallbacks) {
    cb.reject(new Error("Worker was terminated"));
    workerCallbacks.delete(id);
  }
}

function getWorker() {
  if (workerInstance) return workerInstance;
  try {
    workerInstance = new Worker(
      `${process.env.PUBLIC_URL || ""}/mediainfo-worker.js`
    );
    workerInstance.onmessage = (e) => {
      const { id, tracks, error, heartbeat, status } = e.data;
      const cb = workerCallbacks.get(id);
      if (!cb) return;

      // Heartbeat — worker is still alive, reset the timeout
      if (heartbeat) {
        cb.resetTimeout();
        return;
      }

      // Status update — log and reset timeout
      if (status) {
        console.info(`[MediaInfo Worker] ${status}`);
        cb.resetTimeout();
        return;
      }

      // Final result — resolve or reject
      workerCallbacks.delete(id);
      if (error) {
        // Kill worker on error — WASM state may be dirty
        console.warn("[MediaInfo Worker] Analysis error — terminating worker for clean slate");
        killWorker();
        cb.reject(new Error(error));
      } else {
        cb.resolve(tracks);
      }
    };
    workerInstance.onerror = (err) => {
      console.error("[MediaInfo Worker] Fatal error:", err);
      killWorker();
    };
  } catch (err) {
    console.warn("[MediaInfo Worker] Could not spawn worker:", err);
    return null;
  }
  return workerInstance;
}

const WORKER_TIMEOUT_MS = 30000; // 30s — worker gets killed and respawned on failure anyway
// Healthy files complete in seconds. If it takes longer than 30s,
// the file is likely corrupt or the worker is stuck.

function analyzeWithWorker(file) {
  return new Promise((resolve, reject) => {
    const worker = getWorker();
    if (!worker) return reject(new Error("Worker unavailable"));

    const id = ++workerIdCounter;
    let timer = null;

    const startTimeout = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        workerCallbacks.delete(id);
        // Kill worker on timeout — it's stuck, WASM state is unrecoverable
        console.warn("[MediaInfo Worker] Timed out — terminating worker");
        killWorker();
        reject(new Error(`Video spec validation timed out after ${WORKER_TIMEOUT_MS / 1000}s — the file may be too large. Please try again.`));
      }, WORKER_TIMEOUT_MS);
    };

    startTimeout();

    workerCallbacks.set(id, {
      resolve: (tracks) => {
        clearTimeout(timer);
        resolve(tracks);
      },
      reject: (err) => {
        clearTimeout(timer);
        reject(err);
      },
      resetTimeout: startTimeout,
    });

    worker.postMessage({ id, file });
  });
}

// ============================================================
// IMAGE DIMENSION READER (ROBUST / IFRAME-SAFE)
// ============================================================
export async function getImageDimensions(file) {
  // Preferred modern path
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(file);
      const dims = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      return dims;
    } catch (err) {
      // fall through to legacy loader
    }
  }

  // Legacy fallback (Image + blob)
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image decode failed"));
    };

    img.src = url;
  });
}

// ============================================================
// HELPER: Get expected dimensions from spec
// ============================================================
function getExpectedDimensions(spec, type = "video") {
  // Check spec.dimensions array first (IPTV style)
  if (Array.isArray(spec.dimensions) && spec.dimensions.length > 0) {
    return spec.dimensions;
  }

  // Check type-specific spec (BigScreen style)
  const typeSpec = type === "video" ? spec.videoSpec : spec.imageSpec;
  if (typeSpec?.width && typeSpec?.height) {
    return [{ width: typeSpec.width, height: typeSpec.height }];
  }

  return null;
}

// ============================================================
// HELPER: Format duration for error messages
// ============================================================
function formatDurationMs(sec) {
  if (typeof sec !== "number" || isNaN(sec)) return "unknown";

  const totalMs = Math.round(sec * 1000);
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;

  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
  }

  return `${seconds}.${String(ms).padStart(3, "0")}s`;
}

// ============================================================
// SHARED VIDEO TRACK VALIDATION
// Both main-thread and worker paths produce the same tracks
// array and feed it into this function for spec checking.
// ============================================================
function validateVideoTracks(tracks, spec, ext) {
  const reasons = [];

  const info = {
    type: "video",
    width: null,
    height: null,
    frameRate: null,
    duration: null,
    format: null,
    alpha: { present: false, type: null },
    audio: { present: false },
  };

  const video = tracks.find((t) => t["@type"] === "Video");
  const audio = tracks.find((t) => t["@type"] === "Audio");

  if (!video) {
    return { valid: false, reasons: ["No video track detected in file"], info };
  }

  if (!spec.videoSpec?.allowed) {
    return {
      valid: false,
      reasons: ["Video files are not allowed for this endpoint"],
      info,
    };
  }

  // Extract metadata from tracks
  info.frameRate = Number(video.FrameRate);
  info.duration = video.Duration
    ? Number(video.Duration)
    : video.Duration_String
    ? parseFloat(video.Duration_String)
    : null;
  // Fallback: check General track for duration (some MOV/ProRes files
  // only report duration on the General track, not the Video track)
  if (info.duration == null) {
    const general = tracks.find((t) => t["@type"] === "General");
    if (general?.Duration) info.duration = Number(general.Duration);
  }
  info.format = video.Format || "Unknown";
  info.width = Number(video.Width);
  info.height = Number(video.Height);

  const videoSpec = spec.videoSpec;

  // Extension check
  if (Array.isArray(videoSpec.extensions) && !videoSpec.extensions.includes(ext)) {
    reasons.push(
      `File extension must be ${videoSpec.extensions.join(", ")} (this file is .${ext})`
    );
  }

  // Dimension check
  const expectedDims = getExpectedDimensions(spec, "video");
  if (expectedDims) {
    const match = expectedDims.some(
      (d) => d.width === info.width && d.height === info.height
    );
    if (!match) {
      reasons.push(
        `Resolution must be ${expectedDims
          .map((d) => `${d.width}×${d.height}`)
          .join(" or ")}px (this file is ${info.width}×${info.height}px)`
      );
    }
  }

  // Frame rate check
  if (
    typeof videoSpec.frameRate === "number" &&
    Math.round(info.frameRate) !== videoSpec.frameRate
  ) {
    reasons.push(
      `Frame rate must be ${videoSpec.frameRate}fps (this file is ${Math.round(info.frameRate)}fps)`
    );
  }

  // Duration checks
  if (typeof videoSpec.minDuration === "number" && info.duration != null && info.duration < videoSpec.minDuration) {
    reasons.push(
      `Video is too short: minimum ${formatDurationMs(videoSpec.minDuration)}, ` +
      `this file is ${formatDurationMs(info.duration)}`
    );
  }

  if (typeof videoSpec.maxDuration === "number" && info.duration != null && info.duration > videoSpec.maxDuration) {
    reasons.push(
      `Video is too long: maximum ${formatDurationMs(videoSpec.maxDuration)}, ` +
      `this file is ${formatDurationMs(info.duration)}`
    );
  }

  // Codec/format check
  if (
    Array.isArray(videoSpec.formats) &&
    !videoSpec.formats.some((f) => String(info.format).includes(f))
  ) {
    reasons.push(
      `Video codec must be ${videoSpec.formats.join(" or ")} (this file is ${info.format})`
    );
  }

  // ProRes 4444 profile enforcement
  const profile = video.Format_Profile || "";
  if (
    videoSpec.requireProRes4444 &&
    String(info.format).includes("ProRes") &&
    !profile.includes("4444")
  ) {
    reasons.push(
      `ProRes 4444 is required for this placement — this file is ProRes ${profile || "unknown profile"}`
    );
  }

  // Alpha channel detection
  const hasExplicitAlpha =
    video.Alpha === "Yes" ||
    info.format.includes("RGBA") ||
    info.format.includes("YUVA");
  const isProRes4444 =
    info.format.includes("ProRes") && profile.includes("4444");

  if (videoSpec.inspectAlpha && (isProRes4444 || hasExplicitAlpha)) {
    info.alpha.present = true;
    info.alpha.type = isProRes4444 ? "ProRes 4444" : "Alpha";
  }

  if (videoSpec.requiresAlpha && !info.alpha.present) {
    reasons.push("Alpha channel is required but not present");
  }

  // Audio detection
  if (audio) {
    info.audio.present = true;
    info.audio.channels = parseInt(audio.Channels, 10) || null;
    info.audio.sampleRate = parseInt(audio.SamplingRate, 10) || null;
  }

  if (videoSpec.audio?.mode === "required" && !info.audio.present) {
    reasons.push("An audio track is required but none was found");
  }

  return { valid: reasons.length === 0, reasons, info };
}

// ============================================================
// BROWSER FALLBACK: HTMLVideoElement metadata extraction
// Last resort when both main-thread MediaInfo and worker fail.
// Validates: dimensions, duration, extension, playability.
// Cannot check: codec, framerate, ProRes profile, alpha, audio.
// ============================================================
const BROWSER_PROBE_TIMEOUT_MS = 15000;

function probeVideoWithBrowser(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;

    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Browser metadata probe timed out"));
    }, BROWSER_PROBE_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      const result = {
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        duration: isFinite(video.duration) ? video.duration : null,
      };
      cleanup();
      resolve(result);
    };

    video.onerror = () => {
      clearTimeout(timeout);
      const code = video.error?.code;
      const msg =
        code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
          ? "Browser cannot decode this file — the container or codec may not be supported"
          : code === MediaError.MEDIA_ERR_DECODE
          ? "File appears to be corrupted or contains unsupported data"
          : "Browser could not read this video file";
      cleanup();
      reject(new Error(msg));
    };

    video.src = url;
  });
}

async function browserFallbackValidation(file, spec, ext) {
  const reasons = [];
  const uncheckedFields = [];

  const info = {
    type: "video",
    width: null,
    height: null,
    frameRate: null,
    duration: null,
    format: ext.toUpperCase(),
    alpha: { present: false, type: null },
    audio: { present: false },
  };

  const videoSpec = spec.videoSpec;

  // Extension check (doesn't need mediainfo)
  if (Array.isArray(videoSpec.extensions) && !videoSpec.extensions.includes(ext)) {
    reasons.push(
      `File extension must be ${videoSpec.extensions.join(", ")} (this file is .${ext})`
    );
  }

  // Probe via HTMLVideoElement
  let probeResult;
  try {
    probeResult = await probeVideoWithBrowser(file);
  } catch (err) {
    // Browser can't decode this file. If the spec requires codecs the browser
    // can't play (ProRes, etc.), we CANNOT verify codec/profile — reject with
    // a clear message rather than silently allowing an unchecked upload.
    // (e.g., ProRes LT would pass through unchecked when spec requires ProRes 4444)
    const browserUnsupportedCodecs = ["ProRes"];
    const specAllowsExoticCodec =
      Array.isArray(videoSpec.formats) &&
      videoSpec.formats.some((f) => browserUnsupportedCodecs.some((c) => f.includes(c)));

    if (specAllowsExoticCodec) {
      console.warn(
        `[MediaInfo] Browser can't decode ${ext.toUpperCase()} and MediaInfo unavailable — cannot verify codec/profile`
      );
      return {
        valid: false,
        reasons: [
          "This file could not be validated — the codec (likely ProRes) cannot be verified by the browser. " +
          "Please try uploading again, or contact your operator if the issue persists."
        ],
        info,
        browserFallback: true,
        uncheckedFields: ["codec", "frameRate", "dimensions", "duration", "alpha", "proResProfile", "audio"],
      };
    }

    return {
      valid: false,
      reasons: [err.message],
      info,
      browserFallback: true,
      uncheckedFields: ["codec", "frameRate", "alpha", "audio"],
    };
  }

  info.width = probeResult.width;
  info.height = probeResult.height;
  info.duration = probeResult.duration;

  // If the spec requires specific codecs that the browser can't verify,
  // reject rather than letting unchecked files through.
  // (e.g., ProRes 4444 vs ProRes LT — browser can't distinguish these)
  const codecVerificationRequired =
    Array.isArray(videoSpec.formats) && videoSpec.formats.length > 0;
  const browserGotNullDimensions = !info.width || !info.height;

  if (codecVerificationRequired && browserGotNullDimensions) {
    // Browser couldn't fully decode this file (e.g., ProRes on Chrome).
    // We have no dimensions and can't verify codec — fail with clear message.
    return {
      valid: false,
      reasons: [
        "This file could not be fully validated — the browser could not read its dimensions or codec. " +
        "Please try uploading again. If the issue persists, the file may be corrupt."
      ],
      info,
      browserFallback: true,
      uncheckedFields: ["codec", "frameRate", "dimensions", "alpha", "audio"],
    };
  }

  // Dimension check
  const expectedDims = getExpectedDimensions(spec, "video");
  if (expectedDims && info.width && info.height) {
    const match = expectedDims.some(
      (d) => d.width === info.width && d.height === info.height
    );
    if (!match) {
      reasons.push(
        `Resolution must be ${expectedDims
          .map((d) => `${d.width}×${d.height}`)
          .join(" or ")}px (this file is ${info.width}×${info.height}px)`
      );
    }
  }

  // Duration checks
  if (info.duration != null) {
    if (typeof videoSpec.minDuration === "number" && info.duration < videoSpec.minDuration) {
      reasons.push(
        `Video is too short: minimum ${formatDurationMs(videoSpec.minDuration)}, ` +
        `this file is ${formatDurationMs(info.duration)}`
      );
    }
    if (typeof videoSpec.maxDuration === "number" && info.duration > videoSpec.maxDuration) {
      reasons.push(
        `Video is too long: maximum ${formatDurationMs(videoSpec.maxDuration)}, ` +
        `this file is ${formatDurationMs(info.duration)}`
      );
    }
  }

  // Fields that browser can't verify
  uncheckedFields.push("codec");
  if (typeof videoSpec.frameRate === "number") uncheckedFields.push("frameRate");
  if (videoSpec.inspectAlpha || videoSpec.requiresAlpha) uncheckedFields.push("alpha");
  if (videoSpec.requireProRes4444) uncheckedFields.push("proResProfile");
  if (videoSpec.audio?.mode === "required") uncheckedFields.push("audio");

  console.info(
    `[MediaInfo] Browser fallback validated ${file.name}: ` +
    `${info.width}×${info.height}, ${info.duration?.toFixed(2)}s. ` +
    `Not verified: ${uncheckedFields.join(", ")}`
  );

  return {
    valid: reasons.length === 0,
    reasons,
    info,
    browserFallback: true,
    uncheckedFields,
  };
}

// ============================================================
// FILE VALIDATION (main entry point)
// ============================================================
export async function validateFile(file, spec) {
  const ext = file.name.split(".").pop().toLowerCase();

  const info = {
    type: null,
    width: null,
    height: null,
    frameRate: null,
    duration: null,
    format: null,
    alpha: { present: false, type: null },
    audio: { present: false },
  };

  // File size cap — use spec.maxFileSize if set, otherwise default 2GB
  // Individual specs can set their own maxFileSize (e.g. 10GB for big screen content)
  const MAX_BYTES = spec.maxFileSize || 2 * 1024 * 1024 * 1024;
  const maxLabel = MAX_BYTES >= 1024 * 1024 * 1024
    ? `${(MAX_BYTES / (1024 * 1024 * 1024)).toFixed(0)}GB`
    : `${(MAX_BYTES / (1024 * 1024)).toFixed(0)}MB`;
  if (file.size > MAX_BYTES) {
    return {
      valid: false,
      reasons: [
        `File size exceeds ${maxLabel} limit (actual: ${(file.size / 1024 / 1024).toFixed(1)} MB)`,
      ],
      info,
    };
  }

  // ============================================================
  // IMAGE VALIDATION
  // ============================================================
  if (["jpg", "jpeg", "png"].includes(ext)) {
    if (!spec.imageSpec?.allowed) {
      return {
        valid: false,
        reasons: ["Images are not allowed for this endpoint"],
        info,
      };
    }

    // Image extension check
    if (
      Array.isArray(spec.imageSpec.extensions) &&
      !spec.imageSpec.extensions.includes(ext)
    ) {
      return {
        valid: false,
        reasons: [
          `File extension mismatch: expected ${spec.imageSpec.extensions.join(", ")}, detected .${ext}`,
        ],
        info,
      };
    }

    info.type = "image";

    try {
      const { width, height } = await getImageDimensions(file);
      info.width = width;
      info.height = height;
    } catch {
      return {
        valid: false,
        reasons: ["Image dimensions could not be read"],
        info,
      };
    }

    // Image dimension check
    const expectedDims = getExpectedDimensions(spec, "image");
    if (expectedDims) {
      const match = expectedDims.some(
        (d) => d.width === info.width && d.height === info.height
      );

      if (!match) {
        return {
          valid: false,
          reasons: [
            `Resolution must be ${expectedDims
              .map((d) => `${d.width}×${d.height}`)
              .join(" or ")}px (this file is ${info.width}×${info.height}px)`,
          ],
          info,
        };
      }
    }

    // Alpha detection — read PNG IHDR color type byte (offset 25)
    if ((spec.imageSpec.inspectAlpha || spec.imageSpec.requiresAlpha) && ext === "png") {
      try {
        const header = await file.slice(0, 26).arrayBuffer();
        const colorType = new Uint8Array(header)[25];
        // 4 = Grayscale + Alpha, 6 = RGBA
        if (colorType === 4 || colorType === 6) {
          info.alpha.present = true;
          info.alpha.type = colorType === 6 ? "RGBA" : "GrayA";
        }
      } catch {
        // If header read fails, leave alpha as not detected
      }
    }

    if (spec.imageSpec.requiresAlpha && !info.alpha.present) {
      return {
        valid: false,
        reasons: ["This placement requires a PNG with an alpha channel (RGBA). The uploaded file has no transparency."],
        info,
      };
    }

    return { valid: true, reasons: [], info };
  }

  // ============================================================
  // VIDEO VALIDATION
  // ============================================================
  const WORKER_SIZE_THRESHOLD = 100 * 1024 * 1024; // 100MB — worker is reliable, keep main thread responsive
  const isLargeFile = file.size >= WORKER_SIZE_THRESHOLD;

  // ---- PATH A: Large files → Web Worker (off main thread) ----
  if (isLargeFile) {
    console.info(
      `[MediaInfo] Large file (${(file.size / (1024 * 1024)).toFixed(0)}MB) — routing to Web Worker`
    );

    try {
      const tracks = await analyzeWithWorker(file);
      return validateVideoTracks(tracks, spec, ext);
    } catch (workerErr) {
      // If the worker detected a truncated/corrupt file, surface that directly
      // — no point falling through to browser fallback which will also fail
      if (workerErr.message && (workerErr.message.includes("truncated") || workerErr.message.includes("corrupt"))) {
        console.error("[MediaInfo] Worker detected corrupt file:", workerErr.message);
        return { valid: false, reasons: [workerErr.message], info };
      }

      console.warn("[MediaInfo] Worker failed:", workerErr.message, "— trying browser fallback");

      // Worker failed — fall back to browser probe (partial validation)
      if (spec.videoSpec?.allowed) {
        try {
          return await browserFallbackValidation(file, spec, ext);
        } catch (browserErr) {
          console.error("[MediaInfo] Browser fallback also failed:", browserErr.message);
        }
      }
      return { valid: false, reasons: ["Video metadata could not be read — the file may be too large or corrupted"], info };
    }
  }

  // ---- PATH B: Small files → Main thread MediaInfo ----
  try {
    const MediaInfoFactory = await MediaInfoReady;

    // If main-thread MediaInfo didn't load, try worker instead
    if (!MediaInfoFactory) {
      console.warn("[MediaInfo] Main thread unavailable — routing to Web Worker");
      try {
        const tracks = await analyzeWithWorker(file);
        return validateVideoTracks(tracks, spec, ext);
      } catch (workerErr) {
        // Surface truncated/corrupt file errors directly
        if (workerErr.message && (workerErr.message.includes("truncated") || workerErr.message.includes("corrupt"))) {
          console.error("[MediaInfo] Worker detected corrupt file:", workerErr.message);
          return { valid: false, reasons: [workerErr.message], info };
        }

        console.warn("[MediaInfo] Worker also failed:", workerErr.message, "— trying browser fallback");
        if (spec.videoSpec?.allowed) {
          try {
            return await browserFallbackValidation(file, spec, ext);
          } catch (browserErr) {
            console.error("[MediaInfo] Browser fallback also failed:", browserErr.message);
          }
        }
        return { valid: false, reasons: ["Video metadata could not be read"], info };
      }
    }

    const mi = await MediaInfoFactory({ format: "object" });

    const result = await mi.analyzeData(
      () => file.size,
      (size, offset) =>
        file
          .slice(offset, offset + size)
          .arrayBuffer()
          .then((b) => new Uint8Array(b))
    );

    return validateVideoTracks(result.media.track, spec, ext);
  } catch (err) {
    console.error("MediaInfo main-thread error:", err);

    // Main thread crashed — try worker as fallback
    console.warn("[MediaInfo] Trying Web Worker after main-thread crash");
    try {
      const tracks = await analyzeWithWorker(file);
      return validateVideoTracks(tracks, spec, ext);
    } catch (workerErr) {
      // Surface truncated/corrupt file errors directly
      if (workerErr.message && (workerErr.message.includes("truncated") || workerErr.message.includes("corrupt"))) {
        console.error("[MediaInfo] Worker detected corrupt file:", workerErr.message);
        return { valid: false, reasons: [workerErr.message], info };
      }

      console.warn("[MediaInfo] Worker also failed:", workerErr.message, "— trying browser fallback");
      if (spec.videoSpec?.allowed) {
        try {
          return await browserFallbackValidation(file, spec, ext);
        } catch (browserErr) {
          console.error("[MediaInfo] Browser fallback also failed:", browserErr.message);
        }
      }
      return { valid: false, reasons: ["Video metadata could not be read"], info };
    }
  }
}

const mediaInfoService = { validateFile, getImageDimensions, MediaInfoReady };

export default mediaInfoService;
